/**
 * Admin Events With Stats Hook (URQL)
 *
 * Query hook for fetching events with order/ticket statistics for calendar view.
 */

import { useQuery } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import type { AdminEventWithStats } from "../../shared/types";

// =============================================================================
// QUERY
// =============================================================================

const AdminEventsWithStatsDocument = `
  query AdminEventsWithStats($fromDate: String, $toDate: String, $timezone: String) {
    adminEventsWithStats(fromDate: $fromDate, toDate: $toDate, timezone: $timezone) {
      id
      title
      startsAt
      imageUrl
      venue
      ordersCount
      ticketsCount
      pendingCount
    }
  }
`;

// =============================================================================
// HOOK
// =============================================================================

interface UseAdminEventsWithStatsOptions {
  fromDate?: string;
  toDate?: string;
  timezone?: string;
  pause?: boolean;
}

export function useAdminEventsWithStats(options: UseAdminEventsWithStatsOptions = {}) {
  const { fromDate, toDate, timezone, pause = false } = options;

  const [result, reexecute] = useQuery<{
    adminEventsWithStats: AdminEventWithStats[];
  }>({
    query: AdminEventsWithStatsDocument,
    variables: { fromDate, toDate, timezone },
    pause,
  });

  // Track errors
  if (result.error) {
    errorTracking.addBreadcrumb({
      category: "admin.orders",
      message: "Failed to load events with stats",
      level: "error",
    });
  }

  return {
    events: result.data?.adminEventsWithStats ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}
